const ACCESS_TOKEN_KEY = "bettrQaAccessToken";
const ID_TOKEN_KEY = "bettrQaIdToken";
const VERIFIER_KEY = "bettrQaPkceVerifier";

interface AuthConfig {
  authority: string;
  clientId: string;
  redirectUri: string;
  logoutUri: string;
}

function base64Url(bytes: Uint8Array): string {
  let value = "";
  bytes.forEach((byte) => { value += String.fromCharCode(byte); });
  return btoa(value).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function digest(value: string): Promise<string> {
  return base64Url(new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value))));
}

async function config(): Promise<AuthConfig> {
  const response = await fetch("/api/v1/auth-config");
  if (!response.ok) throw new Error("QA Hub authentication is not configured.");
  return response.json() as Promise<AuthConfig>;
}

export function token(): string | null {
  // API Gateway's Cognito authorizer validates the ID token when the method has
  // no OAuth scopes. An access token has `client_id` rather than the required
  // `aud` claim and causes a successful Hosted UI login to bounce back here.
  return sessionStorage.getItem(ID_TOKEN_KEY) ?? sessionStorage.getItem(ACCESS_TOKEN_KEY);
}

export async function signIn(): Promise<void> {
  const auth = await config();
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(48)));
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  const params = new URLSearchParams({
    client_id: auth.clientId,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: auth.redirectUri,
    code_challenge_method: "S256",
    code_challenge: await digest(verifier),
  });
  window.location.assign(`${auth.authority}/oauth2/authorize?${params}`);
}

export async function finishSignIn(code: string): Promise<void> {
  const auth = await config();
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error("The sign-in session expired. Please sign in again.");
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: auth.clientId,
    code,
    redirect_uri: auth.redirectUri,
    code_verifier: verifier,
  });
  const response = await fetch(`${auth.authority}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!response.ok) throw new Error("Cognito rejected the authorization code.");
  const payload = await response.json() as { access_token: string; id_token: string };
  sessionStorage.setItem(ACCESS_TOKEN_KEY, payload.access_token);
  sessionStorage.setItem(ID_TOKEN_KEY, payload.id_token);
  sessionStorage.removeItem(VERIFIER_KEY);
  history.replaceState({}, "", "/");
}

export async function signOut(): Promise<void> {
  const auth = await config();
  sessionStorage.clear();
  const params = new URLSearchParams({ client_id: auth.clientId, logout_uri: auth.logoutUri });
  window.location.assign(`${auth.authority}/logout?${params}`);
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const authToken = token();
  if (!authToken) throw new Error("AUTH_REQUIRED");
  const response = await fetch(`/api/v1${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
      ...init.headers,
    },
  });
  if (response.status === 401 || response.status === 403) {
    sessionStorage.clear();
    throw new Error("AUTH_REQUIRED");
  }
  const payload = await response.json().catch(() => ({})) as T & { message?: string };
  if (!response.ok) throw new Error(payload.message ?? `Request failed (${response.status})`);
  return payload;
}
